import { Link, Navigate } from 'react-router-dom';
import Login from '@/pages/Login';
import MainLayout from '../layouts/MainLayout';
import PATH from './paths/PATH';
import React from 'react';
import AuthGuard from '@/components/AuthGuard';
import { ROLES } from '@/hooks/useRole';
import { Button, Result } from 'antd';
import PieceStats from '@/pages/PieceStats';
import Dashboard from '@/pages/Dashboard';
import Register from '@/pages/Register';
import PrizeStats from '@/pages/PrizeStats';
import LoginStats from '@/pages/LoginStats';
import PrizeManagement from '@/pages/PrizeManagement';
import PrizeManagementCreate from '@/pages/PrizeManagement/PrizeManagementCreate';
import PrizeManagementUpdate from '@/pages/PrizeManagement/PrizeManagementUpdate';
import ChargeHistory from '@/pages/ChargeHistory';
import RegisterCancelHistory from '@/pages/RegisterCancelHistory';
import FreePlay from '@/pages/FreePlay';
import SoloModePlay from '@/pages/SoloModePlay';
import MultiplayerMode from '@/pages/MultiplayerMode';
import Mission from '@/pages/Mission';
import MissionCreate from '@/pages/Mission/MissionCreate';
import MissionUpdate from '@/pages/Mission/MissionUpdate';
import Topic from '@/pages/Topic';
import TopicCreate from '@/pages/Topic/TopicCreate';
import TopicUpdate from '@/pages/Topic/TopicUpdate';
import TopicConfig from '@/pages/Topic/TopicConfig';
import TopicAnswer from '@/pages/TopicAnswer';
import TopicAnswerCreate from '@/pages/TopicAnswer/TopicAnswerCreate';
import TopicAnswerUpdate from '@/pages/TopicAnswer/TopicAnswerUpdate';
import Wheel from '@/pages/Wheel';
import WheelCreate from '@/pages/Wheel/WheelCreate';
import WheelUpdate from '@/pages/Wheel/WheelUpdate';
import MsisdnBlock from '@/pages/MsisdnBlock';
import MsisdnBlockCreate from '@/pages/MsisdnBlock/MsisdnBlockCreate';
import Configuration from '@/pages/Configuration';
import useUser from '@/redux/hooks/useUser';
import SendSMS from '@/pages/SendSMS';
import SendSMSDetails from '@/pages/SendSMS/SendSMSDetails';
import GameStats from '@/pages/GameStats';
import UserKeyManagement from '@/pages/UserKeyManagement';
import ErrorWrapper from '@/components/common/ErrorWrapper';
import LoginStreak from '@/pages/LoginStreak';
import ReportOpenBox from '@/pages/OpenBox/ReportOpenBox';
import BoxManagement from '@/pages/OpenBox/BoxManagement';
import ThemeManagement from '@/pages/ThemeManagement';
import CreateTheme from '@/pages/ThemeManagement/CreateTheme';
import UpdateTheme from '@/pages/ThemeManagement/UpdateTheme';

const useAppRoutes = () => {
  const { user } = useUser();

  const routes = [
    {
      path: PATH.HOME,
      element: <MainLayout />,
      errorElement: <ErrorWrapper status="500" />,
      children: [
        {
          path: '',
          element:
            user?.role_id === ROLES.ADMIN || user?.role_id === ROLES.DIGITAL ? (
              <Navigate to={PATH.REPORT.ROOT} />
            ) : user?.role_id === ROLES.CUSTOMER_CARE ? (
              <Navigate to={PATH.CUSTOMER_CARE.ROOT} />
            ) : (
              <Navigate to={PATH.LOGIN} />
            ),
        },
        {
          path: PATH.REPORT.ROOT,
          element: <Navigate to={PATH.REPORT.REVENUE} />,
        },
        {
          path: PATH.REPORT.REVENUE,
          element: (
            <AuthGuard allowed={[ROLES.ADMIN, ROLES.DIGITAL]}>
              <Dashboard />
            </AuthGuard>
          ),
        },
        {
          path: PATH.REPORT.REGISTER,
          element: (
            <AuthGuard allowed={[ROLES.ADMIN, ROLES.DIGITAL]}>
              <Register />
            </AuthGuard>
          ),
        },
        {
          path: PATH.REPORT.PIECE_STATS,
          element: (
            <AuthGuard allowed={[ROLES.ADMIN, ROLES.DIGITAL]}>
              <PieceStats />
            </AuthGuard>
          ),
        },
        {
          path: PATH.REPORT.LOGIN_STATS,
          element: (
            <AuthGuard allowed={[ROLES.ADMIN, ROLES.DIGITAL]}>
              <LoginStats />
            </AuthGuard>
          ),
        },
        {
          path: PATH.REPORT.GAME_STATS,
          element: (
            <AuthGuard allowed={[ROLES.ADMIN, ROLES.DIGITAL]}>
              <GameStats />
            </AuthGuard>
          ),
        },
        {
          path: PATH.REPORT.LOGIN_STREAK,
          element: (
            <AuthGuard allowed={[ROLES.ADMIN, ROLES.DIGITAL]}>
              <LoginStreak />
            </AuthGuard>
          ),
        },
        {
          path: PATH.REPORT.OPEN_BOX,
          element: (
            <AuthGuard allowed={[ROLES.ADMIN, ROLES.DIGITAL]}>
              <ReportOpenBox />
            </AuthGuard>
          )
        },
        {
          path: PATH.PRIZE_MANAGEMENT.ROOT,
          element: (
            <AuthGuard allowed={[ROLES.ADMIN]}>
              <PrizeManagement />
            </AuthGuard>
          ),
        },
        {
          path: PATH.BOX_MANAGEMENT,
          element: (
            <AuthGuard allowed={[ROLES.ADMIN]}>
              <BoxManagement />
            </AuthGuard>
          )
        },
        {
          path: PATH.PRIZE_MANAGEMENT.CREATE,
          element: (
            <AuthGuard allowed={[ROLES.ADMIN]}>
              <PrizeManagementCreate />
            </AuthGuard>
          ),
        },
        {
          path: PATH.PRIZE_MANAGEMENT.UPDATE,
          element: (
            <AuthGuard allowed={[ROLES.ADMIN]}>
              <PrizeManagementUpdate />
            </AuthGuard>
          ),
        },
        {
          path: PATH.CUSTOMER_CARE.ROOT,
          element: <Navigate to={PATH.CUSTOMER_CARE.CHARGE_HISTORY} />,
        },
        {
          path: PATH.CUSTOMER_CARE.CHARGE_HISTORY,
          element: (
            <AuthGuard allowed={[ROLES.ADMIN, ROLES.CUSTOMER_CARE, ROLES.DIGITAL]}>
              <ChargeHistory />
            </AuthGuard>
          ),
        },
        {
          path: PATH.CUSTOMER_CARE.REGISTER_CANCEL_HISTORY,
          element: (
            <AuthGuard allowed={[ROLES.ADMIN, ROLES.CUSTOMER_CARE, ROLES.DIGITAL]}>
              <RegisterCancelHistory />
            </AuthGuard>
          ),
        },
        {
          path: PATH.CUSTOMER_CARE.FREE_PLAY,
          element: (
            <AuthGuard allowed={[ROLES.ADMIN, ROLES.CUSTOMER_CARE, ROLES.DIGITAL]}>
              <FreePlay />
            </AuthGuard>
          ),
        },
        {
          path: PATH.CUSTOMER_CARE.SOLO_MODE_PLAY_HISTTORY,
          element: (
            <AuthGuard allowed={[ROLES.ADMIN, ROLES.CUSTOMER_CARE, ROLES.DIGITAL]}>
              <SoloModePlay />
            </AuthGuard>
          ),
        },
        {
          path: PATH.CUSTOMER_CARE.MULTIPLAYER_MODE_PLAY_HISTORY,
          element: (
            <AuthGuard allowed={[ROLES.ADMIN, ROLES.CUSTOMER_CARE, ROLES.DIGITAL]}>
              <MultiplayerMode />
            </AuthGuard>
          ),
        },
        {
          path: PATH.CUSTOMER_CARE.PRIZE_STATS,
          element: (
            <AuthGuard allowed={[ROLES.ADMIN, ROLES.DIGITAL]}>
              <PrizeStats />
            </AuthGuard>
          ),
        },
        {
          path: PATH.USER_KEY_MANAGEMENT.ROOT,
          element: (
            <AuthGuard allowed={[ROLES.ADMIN, ROLES.CUSTOMER_CARE, ROLES.DIGITAL]}>
              <UserKeyManagement />
            </AuthGuard>
          ),
        },
        {
          path: PATH.MISSIONS.ROOT,
          element: (
            <AuthGuard allowed={[ROLES.ADMIN]}>
              <Mission />
            </AuthGuard>
          ),
        },
        {
          path: PATH.MISSIONS.CREATE,
          element: (
            <AuthGuard allowed={[ROLES.ADMIN]}>
              <MissionCreate />
            </AuthGuard>
          ),
        },
        {
          path: PATH.MISSIONS.UPDATE,
          element: (
            <AuthGuard allowed={[ROLES.ADMIN]}>
              <MissionUpdate />
            </AuthGuard>
          ),
        },
        {
          path: PATH.TOPIC.ROOT,
          element: (
            <AuthGuard allowed={[ROLES.ADMIN]}>
              <Topic />
            </AuthGuard>
          ),
        },
        {
          path: PATH.TOPIC.CREATE,
          element: (
            <AuthGuard allowed={[ROLES.ADMIN]}>
              <TopicCreate />
            </AuthGuard>
          ),
        },
        {
          path: PATH.TOPIC.UPDATE,
          element: (
            <AuthGuard allowed={[ROLES.ADMIN]}>
              <TopicUpdate />
            </AuthGuard>
          ),
        },
        {
          path: PATH.TOPIC.CONFIG,
          element: (
            <AuthGuard allowed={[ROLES.ADMIN]}>
              <TopicConfig />
            </AuthGuard>
          ),
        },
        {
          path: PATH.TOPIC_ANSWER.ROOT,
          element: (
            <AuthGuard allowed={[ROLES.ADMIN]}>
              <TopicAnswer />
            </AuthGuard>
          ),
        },
        {
          path: PATH.TOPIC_ANSWER.CREATE,
          element: (
            <AuthGuard allowed={[ROLES.ADMIN]}>
              <TopicAnswerCreate />
            </AuthGuard>
          ),
        },
        {
          path: PATH.TOPIC_ANSWER.UPDATE,
          element: (
            <AuthGuard allowed={[ROLES.ADMIN]}>
              <TopicAnswerUpdate />
            </AuthGuard>
          ),
        },
        {
          path: PATH.WHEEL.ROOT,
          element: (
            <AuthGuard allowed={[ROLES.ADMIN]}>
              <Wheel />
            </AuthGuard>
          ),
        },
        {
          path: PATH.WHEEL.CREATE,
          element: (
            <AuthGuard allowed={[ROLES.ADMIN]}>
              <WheelCreate />
            </AuthGuard>
          ),
        },
        {
          path: PATH.WHEEL.UPDATE,
          element: (
            <AuthGuard allowed={[ROLES.ADMIN]}>
              <WheelUpdate />
            </AuthGuard>
          ),
        },
        {
          path: PATH.MSISDN_BLOCK.ROOT,
          element: (
            <AuthGuard allowed={[ROLES.ADMIN]}>
              <MsisdnBlock />
            </AuthGuard>
          ),
        },
        {
          path: PATH.MSISDN_BLOCK.CREATE,
          element: (
            <AuthGuard allowed={[ROLES.ADMIN]}>
              <MsisdnBlockCreate />
            </AuthGuard>
          ),
        },
        {
          path: PATH.SEND_SMS.ROOT,
          element: (
            <AuthGuard allowed={[ROLES.ADMIN]}>
              <SendSMS />
            </AuthGuard>
          ),
        },
        {
          path: PATH.SEND_SMS.DETAIL,
          element: (
            <AuthGuard allowed={[ROLES.ADMIN]}>
              <SendSMSDetails />
            </AuthGuard>
          ),
        },
        {
          path: PATH.THEME_MANAGEMENT.ROOT,
          element: (
            <AuthGuard allowed={[ROLES.ADMIN]}>
              <ThemeManagement />
            </AuthGuard>
          ),
        },
        {
          path: PATH.THEME_MANAGEMENT.CREATE,
          element: (
            <AuthGuard allowed={[ROLES.ADMIN]}>
              <CreateTheme />
            </AuthGuard>
          ),
        },
        {
          path: PATH.THEME_MANAGEMENT.UPDATE,
          element: (
            <AuthGuard allowed={[ROLES.ADMIN]}>
              <UpdateTheme />
            </AuthGuard>
          ),
        },
        {
          path: PATH.CONFIG,
          element: (
            <AuthGuard allowed={[ROLES.ADMIN]}>
              <Configuration />
            </AuthGuard>
          ),
        },
      ],
    },
    {
      path: PATH.LOGIN,
      element: <Login />,
    },
    {
      path: PATH.UNAUTHORIZED,
      element: <ErrorWrapper status="403" />,
    },
    {
      path: '*',
      element: <ErrorWrapper status="404" />,
    },
  ];

  return routes;
};

export default useAppRoutes;
